/**
 * Sysadmin System Catalog hooks — Studio Phase 3D.
 *
 * Wraps `/api/v1/admin/platform/system-catalog/*`. Used by the
 * sysadmin UI scaffolding at `/sysadmin/studio/*` to manage the four
 * system_* catalog tables.
 *
 * Polished UI is v1.2 — these hooks are intentionally thin: list +
 * single + create + update + delete + version-history + rollback per
 * resource type, plus categories + manifest reload.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Types — match the wire shapes from system-catalog.ts ──────────────────

export interface SysVariable {
  name: string;
  label?: string;
  type: string;
  description?: string;
  required?: boolean;
  default?: string;
  options?: string[];
}

export interface SystemTool {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  sourceType: string;
  sourceConfig: Record<string, unknown>;
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  policy: 'auto_approve' | 'require_approval' | 'forbidden';
  sideEffects: string[];
  category: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SystemSkill {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  instructions: string;
  toolDependencies: string[];
  variables: SysVariable[];
  examples: unknown[];
  category: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SystemPrompt {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  template: string;
  variables: SysVariable[];
  category: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SystemAgent {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  modelBrand: string;
  systemPrompt: string;
  assignedTools: string[];
  assignedSkills: string[];
  kind: 'tool_helper' | 'skill_helper' | 'prompt_helper' | null;
  category: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VersionRow {
  id: string;
  version: number;
  snapshot: Record<string, unknown>;
  changedByAdmin: string | null;
  changedAt: string;
}

export interface SysCategory {
  id: string;
  organizationId: string | null;
  kind: 'tool' | 'skill' | 'prompt' | 'agent';
  name: string;
  description: string | null;
  sortOrder: number;
  isSuggestion: boolean;
  createdAt: string;
  updatedAt: string;
}

const BASE = '/admin/platform/system-catalog';

const KEYS = {
  tools: ['sysadmin-system-catalog', 'tools'] as const,
  tool: (id: string) => ['sysadmin-system-catalog', 'tool', id] as const,
  toolVersions: (id: string) =>
    ['sysadmin-system-catalog', 'tool', id, 'versions'] as const,
  skills: ['sysadmin-system-catalog', 'skills'] as const,
  skill: (id: string) => ['sysadmin-system-catalog', 'skill', id] as const,
  skillVersions: (id: string) =>
    ['sysadmin-system-catalog', 'skill', id, 'versions'] as const,
  prompts: ['sysadmin-system-catalog', 'prompts'] as const,
  prompt: (id: string) => ['sysadmin-system-catalog', 'prompt', id] as const,
  promptVersions: (id: string) =>
    ['sysadmin-system-catalog', 'prompt', id, 'versions'] as const,
  agents: ['sysadmin-system-catalog', 'agents'] as const,
  agent: (id: string) => ['sysadmin-system-catalog', 'agent', id] as const,
  agentVersions: (id: string) =>
    ['sysadmin-system-catalog', 'agent', id, 'versions'] as const,
  categories: ['sysadmin-system-catalog', 'categories'] as const,
};

// ─── Tools ────────────────────────────────────────────────────────────────

export function useSysAdminSystemTools() {
  return useQuery<{ items: SystemTool[] }, Error>({
    queryKey: KEYS.tools,
    queryFn: () => apiClient.get<{ items: SystemTool[] }>(`${BASE}/tools`),
  });
}

export function useSysAdminSystemTool(id: string | null | undefined) {
  return useQuery<{ data: SystemTool }, Error>({
    queryKey: id ? KEYS.tool(id) : ['__skip__'],
    queryFn: () => apiClient.get<{ data: SystemTool }>(`${BASE}/tools/${id}`),
    enabled: !!id,
  });
}

export function useCreateSystemTool() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemTool }, Error, Partial<SystemTool>>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemTool }>(`${BASE}/tools`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tools }),
  });
}

export function useUpdateSystemTool(id: string) {
  const qc = useQueryClient();
  return useMutation<{ data: SystemTool }, Error, Partial<SystemTool>>({
    mutationFn: (body) =>
      apiClient.request<{ data: SystemTool }>(`${BASE}/tools/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.tools });
      qc.invalidateQueries({ queryKey: KEYS.tool(id) });
      qc.invalidateQueries({ queryKey: KEYS.toolVersions(id) });
    },
  });
}

export function useDeleteSystemTool() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: boolean }>(`${BASE}/tools/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.tools }),
  });
}

export function useSystemToolVersions(id: string | null | undefined) {
  return useQuery<{ items: VersionRow[] }, Error>({
    queryKey: id ? KEYS.toolVersions(id) : ['__skip__'],
    queryFn: () =>
      apiClient.get<{ items: VersionRow[] }>(`${BASE}/tools/${id}/versions`),
    enabled: !!id,
  });
}

export function useRollbackSystemTool(id: string) {
  const qc = useQueryClient();
  return useMutation<{ data: SystemTool }, Error, { toVersion: number }>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemTool }>(`${BASE}/tools/${id}/rollback`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.tools });
      qc.invalidateQueries({ queryKey: KEYS.tool(id) });
      qc.invalidateQueries({ queryKey: KEYS.toolVersions(id) });
    },
  });
}

// ─── Skills (parallel) ────────────────────────────────────────────────────

export function useSysAdminSystemSkills() {
  return useQuery<{ items: SystemSkill[] }, Error>({
    queryKey: KEYS.skills,
    queryFn: () => apiClient.get<{ items: SystemSkill[] }>(`${BASE}/skills`),
  });
}

export function useSysAdminSystemSkill(id: string | null | undefined) {
  return useQuery<{ data: SystemSkill }, Error>({
    queryKey: id ? KEYS.skill(id) : ['__skip__'],
    queryFn: () => apiClient.get<{ data: SystemSkill }>(`${BASE}/skills/${id}`),
    enabled: !!id,
  });
}

export function useCreateSystemSkill() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemSkill }, Error, Partial<SystemSkill>>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemSkill }>(`${BASE}/skills`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.skills }),
  });
}

export function useUpdateSystemSkill(id: string) {
  const qc = useQueryClient();
  return useMutation<{ data: SystemSkill }, Error, Partial<SystemSkill>>({
    mutationFn: (body) =>
      apiClient.request<{ data: SystemSkill }>(`${BASE}/skills/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.skills });
      qc.invalidateQueries({ queryKey: KEYS.skill(id) });
    },
  });
}

export function useDeleteSystemSkill() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: boolean }>(`${BASE}/skills/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.skills }),
  });
}

export function useSystemSkillVersions(id: string | null | undefined) {
  return useQuery<{ items: VersionRow[] }, Error>({
    queryKey: id ? KEYS.skillVersions(id) : ['__skip__'],
    queryFn: () => apiClient.get<{ items: VersionRow[] }>(`${BASE}/skills/${id}/versions`),
    enabled: !!id,
  });
}

export function useRollbackSystemSkill(id: string) {
  const qc = useQueryClient();
  return useMutation<{ data: SystemSkill }, Error, { toVersion: number }>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemSkill }>(`${BASE}/skills/${id}/rollback`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.skills });
      qc.invalidateQueries({ queryKey: KEYS.skill(id) });
      qc.invalidateQueries({ queryKey: KEYS.skillVersions(id) });
    },
  });
}

// ─── Prompts ──────────────────────────────────────────────────────────────

export function useSysAdminSystemPrompts() {
  return useQuery<{ items: SystemPrompt[] }, Error>({
    queryKey: KEYS.prompts,
    queryFn: () => apiClient.get<{ items: SystemPrompt[] }>(`${BASE}/prompts`),
  });
}

export function useSysAdminSystemPrompt(id: string | null | undefined) {
  return useQuery<{ data: SystemPrompt }, Error>({
    queryKey: id ? KEYS.prompt(id) : ['__skip__'],
    queryFn: () => apiClient.get<{ data: SystemPrompt }>(`${BASE}/prompts/${id}`),
    enabled: !!id,
  });
}

export function useCreateSystemPrompt() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemPrompt }, Error, Partial<SystemPrompt>>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemPrompt }>(`${BASE}/prompts`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.prompts }),
  });
}

export function useUpdateSystemPrompt(id: string) {
  const qc = useQueryClient();
  return useMutation<{ data: SystemPrompt }, Error, Partial<SystemPrompt>>({
    mutationFn: (body) =>
      apiClient.request<{ data: SystemPrompt }>(`${BASE}/prompts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.prompts });
      qc.invalidateQueries({ queryKey: KEYS.prompt(id) });
    },
  });
}

export function useDeleteSystemPrompt() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: boolean }>(`${BASE}/prompts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.prompts }),
  });
}

export function useSystemPromptVersions(id: string | null | undefined) {
  return useQuery<{ items: VersionRow[] }, Error>({
    queryKey: id ? KEYS.promptVersions(id) : ['__skip__'],
    queryFn: () => apiClient.get<{ items: VersionRow[] }>(`${BASE}/prompts/${id}/versions`),
    enabled: !!id,
  });
}

export function useRollbackSystemPrompt(id: string) {
  const qc = useQueryClient();
  return useMutation<{ data: SystemPrompt }, Error, { toVersion: number }>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemPrompt }>(`${BASE}/prompts/${id}/rollback`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.prompts });
      qc.invalidateQueries({ queryKey: KEYS.prompt(id) });
      qc.invalidateQueries({ queryKey: KEYS.promptVersions(id) });
    },
  });
}

// ─── Agents ───────────────────────────────────────────────────────────────

export function useSysAdminSystemAgents(opts: { kind?: string } = {}) {
  const qs = opts.kind ? `?kind=${encodeURIComponent(opts.kind)}` : '';
  return useQuery<{ items: SystemAgent[] }, Error>({
    queryKey: [...KEYS.agents, opts],
    queryFn: () => apiClient.get<{ items: SystemAgent[] }>(`${BASE}/agents${qs}`),
  });
}

export function useSysAdminSystemAgent(id: string | null | undefined) {
  return useQuery<{ data: SystemAgent }, Error>({
    queryKey: id ? KEYS.agent(id) : ['__skip__'],
    queryFn: () => apiClient.get<{ data: SystemAgent }>(`${BASE}/agents/${id}`),
    enabled: !!id,
  });
}

export function useCreateSystemAgent() {
  const qc = useQueryClient();
  return useMutation<{ data: SystemAgent }, Error, Partial<SystemAgent>>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemAgent }>(`${BASE}/agents`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.agents }),
  });
}

export function useUpdateSystemAgent(id: string) {
  const qc = useQueryClient();
  return useMutation<{ data: SystemAgent }, Error, Partial<SystemAgent>>({
    mutationFn: (body) =>
      apiClient.request<{ data: SystemAgent }>(`${BASE}/agents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.agents });
      qc.invalidateQueries({ queryKey: KEYS.agent(id) });
    },
  });
}

export function useDeleteSystemAgent() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: boolean }>(`${BASE}/agents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.agents }),
  });
}

export function useSystemAgentVersions(id: string | null | undefined) {
  return useQuery<{ items: VersionRow[] }, Error>({
    queryKey: id ? KEYS.agentVersions(id) : ['__skip__'],
    queryFn: () => apiClient.get<{ items: VersionRow[] }>(`${BASE}/agents/${id}/versions`),
    enabled: !!id,
  });
}

export function useRollbackSystemAgent(id: string) {
  const qc = useQueryClient();
  return useMutation<{ data: SystemAgent }, Error, { toVersion: number }>({
    mutationFn: (body) =>
      apiClient.post<{ data: SystemAgent }>(`${BASE}/agents/${id}/rollback`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.agents });
      qc.invalidateQueries({ queryKey: KEYS.agent(id) });
      qc.invalidateQueries({ queryKey: KEYS.agentVersions(id) });
    },
  });
}

// ─── Categories ───────────────────────────────────────────────────────────

export function useSysAdminCategories(kind?: string) {
  const qs = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  return useQuery<{ items: SysCategory[] }, Error>({
    queryKey: [...KEYS.categories, { kind }],
    queryFn: () => apiClient.get<{ items: SysCategory[] }>(`${BASE}/categories${qs}`),
  });
}

export function useCreateSysAdminCategory() {
  const qc = useQueryClient();
  return useMutation<
    { data: SysCategory },
    Error,
    { kind: string; name: string; description?: string; sortOrder?: number }
  >({
    mutationFn: (body) =>
      apiClient.post<{ data: SysCategory }>(`${BASE}/categories`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.categories }),
  });
}

export function useDeleteSysAdminCategory() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: boolean }>(`${BASE}/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.categories }),
  });
}

// ─── Manifest reload ──────────────────────────────────────────────────────

export function useReloadSystemCatalogManifest() {
  const qc = useQueryClient();
  return useMutation<
    {
      counts: {
        categories: number;
        tools: number;
        skills: number;
        prompts: number;
        agents: number;
      };
    },
    Error,
    void
  >({
    mutationFn: () =>
      apiClient.post<{
        counts: {
          categories: number;
          tools: number;
          skills: number;
          prompts: number;
          agents: number;
        };
      }>(`${BASE}/reload`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-system-catalog'] });
    },
  });
}
